<template>
	<div>
		<j-expendable-box class="bg5 b1 bc7 r2">
			<div slot="header" class="pl3 pr3 pt4 pb4 lh125 fs4 c1 fw7">
				{{ $t("benefit_options") }}
			</div>
			<div class="bt1 bc7 p4" slot="body">
				<!-- benefit option list -->
				<j-list :list="computedBenefitOptions" class="b1 r2 bc7">
					<div class="flex xs-12 p3" slot="list" slot-scope="{ item }">
						<!-- Label -->
						<div class="flex xs-6 xs-left xs-middle fw7 c6 lh125">
							{{ item.label }}
						</div>

						<!-- Value -->
						<div class="flex xs-4 xs-left xs-middle lh125">
							{{ item.value }}
						</div>

						<!-- Actions -->
						<div class="flex xs-2 xs-right xs-middle">
							<crm-icon
								name="edit"
								color="c2"
								class="ml2 cu2"
								@click.native="openUpdateBenefitOptionModal(item)"
							/>
							<crm-icon
								name="delete"
								color="cError"
								class="ml2 cu2"
								@click.native="removeBenefitOption(item)"
							/>
						</div>
					</div>
					<!-- no data -->
					<div slot="empty" class="fs3 c6 flex xs-12 xs-middle xs-center p4">
						{{ $t("errors.no_data") }}
					</div>
				</j-list>
				<!-- add -->
				<div class="flex xs-12 xs-right pt2">
					<j-button
						type="primary"
						size="xsmall"
						slot="label"
						@click="openAddBenefitModal()"
						>Add</j-button
					>
				</div>
			</div>
		</j-expendable-box>
		<!-- Add modal -->
		<core-modal
			name="benefitOptionModal"
			headline="Add Benefit Option"
			:width="modalWidth"
			:key="modalWidth"
		>
			<div class="p4">
				<core-select-benefit-option
					:value="benefitGraph"
					:initialBenefitTypeId="initialBenefitTypeId"
					:initialInputValue="initialInputValue"
					:initialInputDataType="initialInputDataType"
					:isLoading="isLoading"
					@benefit:saved="upsertBenefitOption"
				/>
			</div>
		</core-modal>
	</div>
</template>

<script>
import JList from "@/components/JList"
import JButton from "@/components/JButton"
import JExpendableBox from "@/components/JExpendableBox"
import CoreModal from "@/modules/core/components/CoreModal"
import CrmIcon from "@/components/CrmIcon"
import CoreSelectBenefitOption from "@/modules/core/components/CoreSelectBenefitOption"

import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

export default {
	components: {
		JList,
		JButton,
		JExpendableBox,
		CrmIcon,
		CoreSelectBenefitOption,
		CoreModal
	},

	data() {
		return {
			modalWidth: 1200,
			isLoading: false,
			initialBenefitTypeId: null,
			initialInputValue: null,
			initialInputDataType: null,
			benefitOptions: [],
			benefitGraph: [] // All possibles benefits of the product, used to display the graph in the modal
		}
	},

	computed: {
		computedBenefitOptions() {
			return this.benefitOptions?.map(option => {
				return {
					label: option?.name,
					value:
						option?.formattedValue !== null &&
						option?.formattedValue !== undefined
							? option?.formattedValue
							: this.parseScalarValue(option?.value),
					dataType: this.getDataType(option?.value),
					actions: ["update", "remove"],
					typeId: option?.benefitTypeId,
					optionKey: option?.key
				}
			})
		}
	},

	methods: {
		openUpdateBenefitOptionModal(benefitOption) {
			this.initialBenefitTypeId = benefitOption?.typeId
			this.initialInputValue = benefitOption?.value
			this.initialInputDataType = benefitOption?.dataType
			this.modalWidth = 500
			this.$nextTick(() => {
				this.$modal.show("benefitOptionModal")
			})
		},

		openAddBenefitModal() {
			this.initialBenefitTypeId = null
			this.initialInputValue = null
			this.initialInputDataType = null
			this.modalWidth = 1200
			this.$nextTick(() => {
				this.$modal.show("benefitOptionModal")
			})
		},

		// Get options for this offer from the store
		fetchBenefitOptions() {
			this.benefitOptions =
				this.$store?.state?.caseManagement?.proposals
					?.find(
						proposal =>
							proposal.id === this.$store?.state?.caseManagement?.proposalId
					)
					?.basket?.find(
						offer => offer.id === this.$store?.state?.caseManagement?.offerId
					)?.benefitOptions || []
		},

		// Fetch all the available benefits for the product
		// Used to display the graph in the add component, to choose an option
		async fetchBenefitGraph() {
			this.isLoading = true
			const productId = this.$store?.state?.caseManagement?.proposals
				?.find(
					proposal =>
						proposal.id === this.$store?.state?.caseManagement?.proposalId
				)
				?.basket?.find(
					offer => offer.id === this.$store?.state?.caseManagement?.offerId
				)?.product?.productId

			const query = gql`
				fragment Value on scalarValue {
					booleanValue
					dateValue
					stringValue
					numberValue
				}

				fragment TreeMapValues on scalarValue {
					...Value
					arrayValue {
						...Value
						objectValue {
							key
							value {
								...Value
							}
						}
					}
					objectValue {
						key
						value {
							...Value
						}
					}
				}
				fragment BenefitNode on benefitGraph {
					name
					description
					isOptional
					isValueInput
					optionKey
					typeId
					formattedValue: value
					value: value2 {
						...TreeMapValues
					}
				}

				query products_2($where: productWhereInput) {
					products_2(where: $where) {
						list {
							benefitGraph {
								name
								...BenefitNode
								options {
									...BenefitNode
								}
								children {
									...BenefitNode
									options {
										...BenefitNode
									}
									children {
										...BenefitNode
										options {
											...BenefitNode
										}
										children {
											...BenefitNode
											options {
												...BenefitNode
											}
										}
									}
								}
							}
						}
					}
				}
			`

			const variables = {
				where: {
					productId
				}
			}

			const res = await api({ query, variables })

			if (!res?.data?.products_2?.list) {
				this.showErrorMessage(res, res?.data?.products_2)
			} else {
				this.benefitGraph = res?.data?.products_2?.list[0]?.benefitGraph
			}
			this.isLoading = false
		},

		// Return non-null value
		parseScalarValue(val) {
			if (!val) return null
			if (val.numberValue !== undefined && val.numberValue !== null)
				return val.numberValue
			if (val.stringValue !== undefined && val.stringValue !== null)
				return val.stringValue
			if (val.dateValue !== undefined && val.dateValue !== null)
				return val.dateValue
			if (val.booleanValue !== undefined && val.booleanValue !== null)
				return val.booleanValue
			if (val.arrayValue !== undefined && val.arrayValue !== null)
				return val.arrayValue
			if (val.objectValue !== undefined && val.objectValue !== null)
				return val.objectValue
			return null
		},

		// Return non-null value
		getDataType(val) {
			if (!val) return null
			if (val.numberValue !== undefined && val.numberValue !== null)
				return "numberValue"
			if (val.stringValue !== undefined && val.stringValue !== null)
				return "stringValue"
			if (val.dateValue !== undefined && val.dateValue !== null)
				return "dateValue"
			if (val.booleanValue !== undefined && val.booleanValue !== null)
				return "booleanValue"
			if (val.arrayValue !== undefined && val.arrayValue !== null)
				return "arrayValue"
			if (val.objectValue !== undefined && val.objectValue !== null)
				return "objectValue"
			return null
		},

		// Add benefit option and update cache
		async upsertBenefitOption(option) {
			this.isLoading = true
			const query = gql`
				mutation upsertBenefitOption(
					$caseId: String!
					$proposalId: String!
					$offerId: String!
					$input: benefitOptionInput!
				) {
					upsertBenefitOptionOfProposalOffer(
						caseId: $caseId
						proposalId: $proposalId
						offerId: $offerId
						input: $input
					) {
						errors
						status
						errors_2 {
							code
							message
						}
					}
				}
			`

			const variables = {
				caseId: this.$store?.state?.caseManagement?.caseId,
				proposalId: this.$store?.state?.caseManagement?.proposalId,
				offerId: this.$store?.state?.caseManagement?.offerId,
				input: {
					typeId: option.typeId,
					key: option.optionKey,
					value: option.value
				}
			}

			const res = await api({ query, variables })

			if (res?.data?.upsertBenefitOptionOfProposalOffer?.status !== "success") {
				this.showErrorMessage(
					res,
					res?.data?.upsertBenefitOptionOfProposalOffer
				)
			} else {
				// Upsert in cache cache
				if (
					!this.benefitOptions.find(
						benOpt => benOpt.benefitTypeId === option.typeId
					)
				) {
					// Add option to offer benefit options
					const stateFilter = function(state) {
						return state.proposals
							?.find(proposal => proposal.id === state.proposalId)
							?.basket?.find(offer => offer.id === state.offerId)
					}
					this.$store.commit("caseManagement/UPDATE_CASE", {
						actionType: "push",
						fieldName: "benefitOptions",
						value: {
							benefitTypeId: option.typeId,
							formattedValue: option.formattedValue,
							value: option.value,
							key: option.optionKey,
							name: option.name,
							description: option.description
						},
						stateFilter
					})
				} else {
					// Find and update
					const stateFilter = function(state) {
						return state.proposals
							?.find(proposal => proposal.id === state.proposalId)
							?.basket?.find(offer => offer.id === state.offerId)
							?.benefitOptions?.find(
								benOpt => benOpt.benefitTypeId === option.typeId
							)
					}
					this.$store.commit("caseManagement/UPDATE_CASE", {
						actionType: "update",
						fieldName: "formattedValue",
						value: option.formattedValue,
						stateFilter
					})
					this.$store.commit("caseManagement/UPDATE_CASE", {
						actionType: "update",
						fieldName: "value",
						value: option.value,
						stateFilter
					})
				}
				// Fetch benefit options
				this.fetchBenefitOptions()
			}
			this.$modal.hide("benefitOptionModal")
			this.isLoading = false
		},

		// Remove benefit option and update cache
		async removeBenefitOption(option) {
			this.isLoading = true
			const query = gql`
				mutation removeBenefitOption(
					$caseId: String!
					$proposalId: String!
					$offerId: String!
					$typeId: String!
				) {
					removeBenefitOptionFromProposalOffer(
						caseId: $caseId
						proposalId: $proposalId
						offerId: $offerId
						typeId: $typeId
					) {
						errors
						status
						errors_2 {
							code
							message
						}
					}
				}
			`

			const variables = {
				caseId: this.$store?.state?.caseManagement?.caseId,
				proposalId: this.$store?.state?.caseManagement?.proposalId,
				offerId: this.$store?.state?.caseManagement?.offerId,
				typeId: option.typeId
			}

			const res = await api({ query, variables })

			if (
				res?.data?.removeBenefitOptionFromProposalOffer?.status !== "success"
			) {
				this.showErrorMessage(
					res,
					res?.data?.removeBenefitOptionFromProposalOffer
				)
			} else {
				// Remove option from cache
				const stateFilter = function(state) {
					return state.proposals
						?.find(proposal => proposal.id === state.proposalId)
						?.basket?.find(offer => offer.id === state.offerId)?.benefitOptions
				}
				this.$store.commit("caseManagement/UPDATE_CASE", {
					actionType: "remove",
					fieldName: "benefitTypeId",
					value: option.typeId,
					stateFilter
				})
				// Fetch benefit options
				this.fetchBenefitOptions()
			}
			this.isLoading = false
		},

		showErrorMessage(res, data) {
			this.$store.dispatch("addMessage", {
				message: {
					type: "error",
					text: res?.errors
						? res?.errors
						: data?.errors
						? data?.errors
						: data?.errors_2?.message
						? data?.errors_2?.message
						: "Oops ! An error occured."
				}
			})
		}
	},

	created() {
		this.fetchBenefitOptions()
		this.fetchBenefitGraph()
	}
}
</script>
